/**
 * ChannelTree - Virtual tree using react-arborist for channel navigation.
 * Sections: Channels (with create button), Direct Messages (with create DM button), Archived link.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tree } from 'react-arborist';
import { useChat } from '../../contexts/ChatContext';
import ChannelTreeNode from './ChannelTreeNode';
import ChannelSettingsModal from './ChannelSettingsModal';
import CreateDMModal from './CreateDMModal';
import ArchivedChannelsPanel from './ArchivedChannelsPanel';
import type { ChannelTreeNode as TreeNodeType } from '../../types/chat';

interface TreeData {
  id: string;
  name: string;
  children?: TreeData[];
  data: TreeNodeType;
}

function toTreeData(nodes: TreeNodeType[]): TreeData[] {
  return nodes.map(node => ({
    id: node.id,
    name: node.name,
    data: node,
    children: node.children.length > 0 ? toTreeData(node.children) : undefined,
  }));
}

const ChannelTree: React.FC = () => {
  const { t } = useTranslation('chat');
  const { channelTree, currentChannel, setCurrentChannel, unreadCounts } = useChat();

  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showCreateDM, setShowCreateDM] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [createSubChannelParentId, setCreateSubChannelParentId] = useState<string | null>(null);

  const { channelNodes, dmNodes } = useMemo(() => {
    const channels: TreeNodeType[] = [];
    const dms: TreeNodeType[] = [];
    for (const node of channelTree) {
      if (node.channel_type === 'direct_message') {
        dms.push(node);
      } else {
        channels.push(node);
      }
    }
    return { channelNodes: toTreeData(channels), dmNodes: toTreeData(dms) };
  }, [channelTree]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = useCallback((nodes: any[]) => {
    if (nodes.length > 0 && nodes[0].data?.data) {
      setCurrentChannel(nodes[0].data.data);
    }
  }, [setCurrentChannel]);

  const sectionHeaderClass = "text-xs uppercase tracking-wide text-gray-400 px-3 py-2 flex items-center justify-between";
  const addBtnClass = "text-gray-400 hover:text-white p-0.5 rounded";

  return (
    <div className="h-full flex flex-col">
      {/* Channels section */}
      <div className={sectionHeaderClass}>
        <span>{t('channels')}</span>
        <button
          onClick={() => setShowCreateChannel(true)}
          className={addBtnClass}
          title={t('newChannel')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <Tree
          data={channelNodes}
          openByDefault={true}
          width="100%"
          indent={16}
          rowHeight={32}
          selection={currentChannel?.id}
          onSelect={handleSelect}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(props: any) => (
            <ChannelTreeNode
              {...props}
              unreadCount={unreadCounts.get(props.node?.data?.id)?.unread_count || 0}
              mentionCount={unreadCounts.get(props.node?.data?.id)?.mention_count || 0}
              onCreateSubChannel={(parentId: string) => setCreateSubChannelParentId(parentId)}
            />
          )}
        </Tree>
      </div>

      {/* Direct Messages section */}
      <div className={sectionHeaderClass}>
        <span>{t('directMessages')}</span>
        <button
          onClick={() => setShowCreateDM(true)}
          className={addBtnClass}
          title={t('newDirectMessage')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <Tree
          data={dmNodes}
          openByDefault={true}
          width="100%"
          indent={16}
          rowHeight={32}
          selection={currentChannel?.id}
          onSelect={handleSelect}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(props: any) => (
            <ChannelTreeNode
              {...props}
              unreadCount={unreadCounts.get(props.node?.data?.id)?.unread_count || 0}
              mentionCount={unreadCounts.get(props.node?.data?.id)?.mention_count || 0}
            />
          )}
        </Tree>
      </div>

      {/* Archived channels link */}
      <button
        onClick={() => setShowArchived(true)}
        className="text-xs text-gray-500 hover:text-gray-300 px-3 py-2 text-left"
      >
        {t('archivedChannels')}
      </button>

      {/* Modals */}
      {showCreateChannel && (
        <ChannelSettingsModal onClose={() => setShowCreateChannel(false)} />
      )}
      {createSubChannelParentId && (
        <ChannelSettingsModal
          parentId={createSubChannelParentId}
          onClose={() => setCreateSubChannelParentId(null)}
        />
      )}
      {showCreateDM && (
        <CreateDMModal onClose={() => setShowCreateDM(false)} />
      )}
      {showArchived && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md h-96 text-gray-900">
            <ArchivedChannelsPanel onClose={() => setShowArchived(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelTree;
